
import React, { useRef, useCallback} from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { type ColDef, themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css'
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';

const Wrapper = styled.div<{ height: string }>`
    height: ${({ height }) => height};
    width: 100%;
    margin-bottom: 5px;
`;

type SingleRow = {
    id: number;
    [key: string]: any
}

type Props = {
    rows: SingleRow[];
    columns: ColDef[];
    height: string;
    mode: any;
}

export const DataGrid: React.FC<Props> = ({
    rows,
    columns,
    height,
    mode
}) => {
    const grid = useRef<AgGridReactType>(null);
    // Guard so we only trigger the "initial selection -> PV rebuild" once per mount.
    // After moving generateArrays out of processMiner, apiProvider.getInitialLoad()
    // can be false by the time the grid first renders, which prevents the initial
    // PV diagram build even though rows are marked selected.
    const didApplyInitialSelection = useRef(false);
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const menuIcon = (symbol: string) => {
        if (typeof document === 'undefined') return `<span class="pa-ag-menu-icon">${symbol}</span>`;
        const el = document.createElement('span');
        el.className = 'pa-ag-menu-icon';
        el.textContent = symbol;
        el.setAttribute('aria-hidden', 'true');
        return el;
    };
    const menuLabel = (symbol: string, label: string) => label;
    
    const onRowClicked = (params) => {
        if (params.api.getSelectedRows().length > 100) {
            apiProvider.alertMessage({message: 'A maximum of 100 variants can be selected', severity: 'error', showAlert: true, timeOutActive: true, component: 'variant_slicer'} );
        } else {
            let selectedRows = [...params.api.getSelectedRows()]
            apiProvider.setVariantSlicerSelectedData({ selection: selectedRows , source: 'variants RC', mode: mode.mode});
        }
    };

    const onRowDataUpdated  = (params) => {
            if (apiProvider.getVariantSlicerGridState() != null ) {
                params.api.applyColumnState({state: apiProvider.getVariantSlicerGridState()});
            }
            if (apiProvider.getVariantSlicerGridState() != null ) {
                params.api.setFilterModel(apiProvider.getVariantSlicerGridFilterState());
            }
            params.api.forEachNode((rowNode, index) => {
                if (rowNode.data.selected == 1) {rowNode.setSelected(true)} else {rowNode.setSelected(false)};
            });            
    }

    const onFirstDataRendered  = (params) => {
        if (apiProvider.getVariantSlicerGridState() != null ) {
            params.api.applyColumnState({state: apiProvider.getVariantSlicerGridState()});
        }
        if (apiProvider.getVariantSlicerGridState() != null ) {
            params.api.setFilterModel(apiProvider.getVariantSlicerGridFilterState());
        }
        if (mode.mode ==='process_miner') {
            params.api.forEachNode((rowNode, index) => {
                if (rowNode.data.selected == 1) {rowNode.setSelected(true)} else {rowNode.setSelected(false)};
            });
            // Always push the current selected rows into the PV diagram on first render.
            // (Do it once per mount to avoid repeated rebuilds.)
            if (!didApplyInitialSelection.current) {
                didApplyInitialSelection.current = true;
                // Defer to next tick so AG Grid has definitely applied the selection state.
                setTimeout(() => {
                    const selected = params.api.getSelectedRows();
                    if (selected && selected.length > 0) {
                        apiProvider.setVariantSlicerSelectedData({ selection: selected, source: 'variants FR', mode: mode.mode });
                    }
                }, 0);
            }
        } else {
            params.api.forEachNode((rowNode, index) => {
                rowNode.setSelected(false);
            });     
        }
    };

    const onDragStopped  = (params) => {
        if (params.api.getCellRanges().length === 0 ) {return}

        if ((params.api.getCellRanges()[0].startRow.rowIndex - params.api.getCellRanges()[0].endRow.rowIndex > 50) || (params.api.getCellRanges()[0].endRow.rowIndex - params.api.getCellRanges()[0].startRow.rowIndex > 50)) {
            apiProvider.alertMessage({message: 'A maximum of 50 variants can be selected', severity: 'error', showAlert: true, timeOutActive: true, component: 'variant_slicer'} );
        } else {

            const draggedRange = params.api.getCellRanges()
            if (draggedRange.length > 0 ) {
                params.api.forEachNode((rowNode, index) => {rowNode.setSelected(false)});
                params.api.forEachNode((rowNode, index) => {
                    for (let i = 0; i < draggedRange.length; i++) {
                        if (rowNode.rowIndex != null) {
                            if (draggedRange[i].endRow.rowIndex >= draggedRange[i].startRow.rowIndex) {
                                 if (rowNode.rowIndex >= draggedRange[i].startRow.rowIndex && rowNode.rowIndex <= draggedRange[i].endRow.rowIndex) {
                                    rowNode.setSelected(true);
                                };
                            };
                            if (draggedRange[i].endRow.rowIndex < draggedRange[i].startRow.rowIndex) {
                                if (rowNode.rowIndex <= draggedRange[i].startRow.rowIndex && rowNode.rowIndex >= draggedRange[i].endRow.rowIndex) {
                                    rowNode.setSelected(true);
                                };
                            };    
                        };            
                    };  
                });
                apiProvider.setVariantSlicerSelectedData({ selection: params.api.getSelectedRows() , source: 'variants DS', mode: mode.mode});
            }
        }
    };

    const onSaveGridColumnState = (params) => {
        let columnState = params.api.getColumnState();
        let filterState = params.api.getFilterModel()
        apiProvider.saveVariantSlicerGridState({columnState})
        apiProvider.saveVariantSlicerGridFilterState({filterState})
    }

    const getContextMenuItems = useCallback((params) => {
        var result = [
            {
                name: menuLabel('◆', 'Insights'),
                icon: menuIcon('◆'),
                subMenu: [
                    {
                        // custom item
                        name: menuLabel('●', 'Clusters'),
                        icon: menuIcon('●'),   
                        action: async function () {
                            openProgressModal({status: 'Processing Clusters'})
                            await sleep(200) // needed to display Progress
                            await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setDiagramCluster({source: 'variant'})
                            closeProgressModal()
                        },  
                    },  
                    {
                        // custom item
                        name: menuLabel('↗', 'Influencers'),
                        icon: menuIcon('↗'),
                        action: async function () {
                            openProgressModal({status: 'Processing Influencer Insights'})
                            await sleep(200) // needed to display Progress
                            await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setDiagramInfluenceAnalysis({source: 'variant'})
                            closeProgressModal()
                        },
                    },     
                    {
                        // custom item
                        name: menuLabel('▦', 'Decompose'),
                        icon: menuIcon('▦'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setDiagramDecompositionTree({source: 'variant'})
                        },
                    },
                    {
                        name: menuLabel('◷', 'Activity Analysis'),
                        icon: menuIcon('◷'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setTimeInActivitySource({source: 'variant'})
                            apiProvider.setAction_Time({})
                        },
                    },
                    {
                        name: menuLabel('▥', 'Histogram'),
                        icon: menuIcon('▥'),
                        action: function () {
                            apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'variant-tasks', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.histogram({})
                        },
                    },
                ]
            },

            {
                name: menuLabel('☷', apiProvider.getEntityUpper() + 's'),
                icon: menuIcon('☷'),
                subMenu: [
                    {
                        name: menuLabel('≡', 'Detail'),
                        icon: menuIcon('≡'),
                        action: function () {
                            apiProvider.setVariantDetailProps({variant: params.node.data.variant_id})
                            apiProvider.taskDetail({})
                        },
                    },
                    {
                        // custom item
                        name: menuLabel('↻', 'History'),
                        icon: menuIcon('↻'),   
                        action: function () {
                            apiProvider.processAnalytics({variant: params.node.data.variant_id.toString(), object_type: 'Variant', object_name: params.node.data.variant, series_name: apiProvider.getEntityUpper() + 's'})
                        },
                    },    
                    {
                        name: menuLabel('⟳', 'LifeCycle'),
                        icon: menuIcon('⟳'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setTasksVisualizationSource({source: 'variant'})
                            apiProvider.tasksVisualization()
                            apiProvider.taskLifecycle({})
                        },
                    },

                ],
            },       
            {
                name: menuLabel('◇', 'Events'),
                icon: menuIcon('◇'),
                subMenu: [
                    {
                        name: menuLabel('≡', 'Detail'),
                        icon: menuIcon('≡'),
                        action: function () {
                            apiProvider.setVariantDetailProps({variant: params.node.data.variant_id })
                            apiProvider.eventDetail({})
                        },
                    },
                    {
                        name: menuLabel('▥', 'Histogram'),
                        icon: menuIcon('▥'),
                        action: function () {
                            apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'variant-events', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.histogram({})
                        },
                    },                  
                ],
            },                          
            {
                name: menuLabel('▽', 'Filter'),
                icon: menuIcon('▽'),
                subMenu: [
                    {
                        name: menuLabel('✓', 'Variant'),
                        icon: menuIcon('✓'),
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'variant'} )
                        },
                    },  
                    {
                        name: menuLabel('=', 'Same steps'),
                        icon: menuIcon('='),
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'same_steps'} )
                        },
                    },                  
                    {
                        name: menuLabel('≈', 'Similar steps'),
                        icon: menuIcon('≈'),
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'similar_steps'} )
                        },
                    },    
                    {
                        name: menuLabel('⇄', 'Similar sequence'),
                        icon: menuIcon('⇄'),
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'similar_sequence'} )
                        },
                    },                                     
                ],
            },    
            'separator',
            {
                name: menuLabel('⇩', 'Export'),
                icon: menuIcon('⇩'),
                subMenu: [
                    {
                        name: menuLabel('CSV', 'CSV Export (all)'),
                        icon: menuIcon('CSV'),
                        action: () => params.api.exportDataAsCsv(
                            {onlySelected: false}
                          ),
                    },
                    {
                        name: menuLabel('CSV', 'CSV Export (selected)'),
                        icon: menuIcon('CSV'),
                        action: () => params.api.exportDataAsCsv(
                            {onlySelected: true}
                          ),
                    },
                    {
                        name: menuLabel('XLS', 'XLS Export (all)'),
                        icon: menuIcon('XLS'),
                        action: () => params.api.exportDataAsExcel(
                            {onlySelected: false}
                          ),
                    },
                    {
                        name: menuLabel('XLS', 'XLS Export (selected)'),
                        icon: menuIcon('XLS'),
                        action: () => params.api.exportDataAsExcel(
                            {onlySelected: true}
                          ),
                    },
                ],
            },        
        ];
        return result;
      }, []);

    const getRowHeight = () => 25;
    
    return (
        <Wrapper height={height}>
            <div
                style={{
                    height: '100%',
                    width: '100%',
                }}
            >
                <AgGridReact 
                    theme={themeAlpine} 
                    rowSelection="multiple"
                    onFirstDataRendered={onFirstDataRendered}
                    onRowDataUpdated={onRowDataUpdated}
                    onRowClicked={onRowClicked}
                    onDragStopped={onDragStopped}
                    getRowHeight={getRowHeight}
                    getContextMenuItems={getContextMenuItems}
                    suppressScrollOnNewData={true}
                    headerHeight={40}
                    onFilterChanged={onSaveGridColumnState}
                    onColumnVisible={onSaveGridColumnState}
                    onColumnResized={onSaveGridColumnState}
                    onSortChanged={onSaveGridColumnState}
                    ref={grid}
                    columnDefs={columns}
                    defaultColDef={{
                        suppressMenu: true,
                        sortable: true,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    sideBar={
                        {
                            toolPanels: [
                                {
                                    id: 'columns',
                                    labelDefault: 'Columns',
                                    labelKey: 'columns',
                                    iconKey: 'columns',
                                    toolPanel: 'agColumnsToolPanel',
                                    toolPanelParams: {
                                        suppressRowGroups: true,
                                        suppressValues: true,
                                        suppressPivotMode: true, 
                                    }
                      
                                },
                                {
                                    id: 'filters',
                                    labelDefault: 'Filters',
                                    labelKey: 'filters',
                                    iconKey: 'filter',
                                    toolPanel: 'agFiltersToolPanel',
                                },
                            ],
                            hiddenByDefault: false,
                            defaultToolPanel: '',
                        }
                    }
                    animateRows={true}
                    rowData={rows}
                    cellSelection={true}
                    tooltipShowDelay={10}
                    tooltipHideDelay={2000}/>
                    
            </div>
        </Wrapper>
    );
};
